import { theme } from 'antd';
import type { Produto } from '../produtos';

/**
 * O produto com cara, nas telas em que ele é o assunto.
 *
 * Até 02/09/2026, plano e assinatura mostravam cada produto como um nome com
 * um ícone verde de "certo" ao lado - o mesmo ícone nos quatro. Um catálogo em
 * que os itens são indistinguíveis não parece catálogo, parece lista de
 * permissões; e a tela onde se decide gastar novecentos reais não pode parecer
 * uma tela de permissões.
 *
 * A arte já existia e já era usada na vitrine da Home. Ela volta aqui em dois
 * tamanhos - **capa** onde o produto está à venda, **miniatura** onde ele só
 * está sendo listado -, e em nenhum dos dois ela é cortada nem coberta.
 *
 * **Por que não o painel da Voa, com o preço por cima da arte.** Aquilo
 * funciona porque a arte da Voa é um degradê abstrato, feito para receber
 * texto. As nossas capas são logotipos escuros sobre quase branco: um véu
 * escuro por cima transformaria as duas coisas em lama, e o preço em branco
 * brigaria com a própria palavra da capa. O preço fica **embaixo** da capa,
 * como na lombada de um livro - e é onde ele sempre esteve em vitrine que
 * vende produto com embalagem.
 */

/** A proporção em que as capas foram desenhadas. Fora dela, corta. */
const PROPORCAO = 16 / 9;

/** A capa em tamanho de vitrine, inteira, dentro de uma moldura discreta. */
export function CapaDoProduto({ produto }: { produto: Produto }) {
  const { token } = theme.useToken();

  return (
    <div
      style={{
        aspectRatio: `${PROPORCAO}`,
        borderRadius: token.borderRadius,
        overflow: 'hidden',
        /*
         * A moldura existe por causa do modo escuro: a capa é quase branca, e
         * sem um fio em volta ela flutua como um retângulo aceso no meio de um
         * cartão escuro. Com o fio, lê como embalagem.
         */
        border: `1px solid ${token.colorBorderSecondary}`,
        // Enquanto a imagem carrega, o retângulo já tem a cor do produto.
        background: produto.acento,
      }}
    >
      <img
        src={produto.capa}
        alt={`Capa de ${produto.nome}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
}

/** A mesma capa, pequena, para o lado do nome numa lista. */
export function MiniaturaDoProduto({
  produto,
  largura = 112,
  apagada,
}: {
  produto: Produto;
  largura?: number;
  /** Acesso encerrado: a capa desbota, e o desbotado é a própria mensagem. */
  apagada?: boolean;
}) {
  const { token } = theme.useToken();

  return (
    <img
      src={produto.capa}
      alt=""
      aria-hidden
      style={{
        width: largura,
        // A altura sai da proporção da arte: qualquer outra cortaria a palavra
        // que a capa tem no meio, e capa com palavra cortada é defeito visível.
        height: Math.round(largura / PROPORCAO),
        flexShrink: 0,
        objectFit: 'cover',
        display: 'block',
        borderRadius: token.borderRadius,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: produto.acento,
        filter: apagada ? 'grayscale(0.85)' : undefined,
        opacity: apagada ? 0.55 : undefined,
      }}
    />
  );
}
